import './AssetEditForm.scss';

import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Alert, Modal, Progress, Select } from 'antd'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Card, Row, Col, Button, Form, Table, Badge } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import AppLoader from '../common/AppLoader'
import AppSearch from '../common/AppSearch'
import DragAndDropInput from '../generics/DragAndDropInput'
import { BdaDataset } from '../../models/BdaDataset'
import { BdaModel, BdaModelDTO } from '../../models/BdaModel'
import { Datasource } from '../../models/Datasource'
import { useFullScreenLoader } from '../../contexts/fullScreenLoader'
import { useToasts } from '../../contexts/toasts'
import { Datasets } from '../../api/Datasets'
import { v4 } from 'uuid'
import { Datasources } from '../../api/Datasources'
import { Models } from '../../api/Models'
import { createQueryDetails, getConnectionDetails } from '../../utils'
import { VizClient } from '../../api/VizClient'
import Tree, { TreeElement } from '../generics/Tree'
import { cloneDeep, set } from 'lodash'
import classNames from 'classnames'
import IconButton from '../common/IconButton'
import UserItem from '../common/UserItem'
import { Framework } from '../../models/BdaService';
import { Frameworks } from '../../api/Frameworks';

const columnTypes = [
  { label: 'String', value: 'String' },
  { label: 'Varchar', value: 'Varchar' },
  { label: 'Number', value: 'Number' },
  { label: 'Int', value: 'Int' },
  { label: 'Boolean', value: 'Boolean' },
  { label: 'Double', value: 'Double' },
  { label: 'Float', value: 'Float' },
  { label: 'Date', value: 'Date' },
];

interface AssetEditFormProps {
  modeLabel: string;
  mode: 'model' | 'dataset';
  asset: Partial<BdaDataset> | Partial<BdaModel>;
}

export default function AssetEditForm({ modeLabel, mode, asset }: AssetEditFormProps) {
  const navigate = useNavigate();

  const [validated, setValidated] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [selectedDatasource, setSelectedDatasource] = useState<Datasource>();
  const [progressLoading, setProgressLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [percent, setPercent] = useState<number>();
  const [searchText, setSearchText] = useState("");
  const [datasources, setDatasources] = useState<Datasource[]>();
  const [selectedTable, setSelectedTable] = useState<any>();
  const [columns, setColumns] = useState<any[]>();
  const [frameworks, setFrameworks] = useState<Framework[]>();
  const [selectedFramework, setSelectedFramework] = useState<number>();
  const [selectedPath, setSelectedPath] = useState<string>();
  const [type, setType] = useState("table");
  const [editingAsset, setEditingAsset] = useState<Partial<BdaModel | BdaDataset> | undefined>(asset);
  const [datasetSettingsError, setDatasetSettingsError] = useState(false);
  const [tables, setTables] = useState<any[]>();
  const [editing, setEditing] = useState<number>();
  const [directoryTree, setDirectoryTree] = useState<TreeElement[]>();
  const [editingColumn, setEditingColumn] = useState<any>();
  const [formatOptions, setFormatOptions] = useState<{ label: string, value: string }[]>([]);
  const [originalColumns, setOriginalColumns] = useState<any[]>();
  const datasourceContainer = useRef<HTMLDivElement | null>(null);
  const treeContainer = useRef<HTMLDivElement | null>(null);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const fileUploaded = useMemo(() => percent === 100, [percent]);
  const frameworksOptions = useMemo(() => frameworks?.map(f => ({ label: f.name, value: f.id, image: f.imageUrl, trailing: f.version })) || [], [frameworks]);

  const initDatasources = useCallback(() => {
    Datasources.getDatasources()
      .then(
        ({ data }) => setDatasources(data),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasources-error', message, `Datasources Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  const initFrameworks = useCallback(() => {
    Frameworks.getFrameworks()
      .then(
        ({ data }) => setFrameworks(data),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('frameworks-error', message, `Frameworks Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  const initAsset = useCallback(() => {
    setSelectedDatasource(asset?.datasource);

    if (mode === 'model') initFrameworks();
    else if ((asset as BdaDataset).datasetFileType && (asset as BdaDataset).datasetFileType!.type === "table") {
      const array = (asset as BdaDataset).datasetFileType!.tableName.split("/");
      const tableName = array[array.length - 1];
      setSelectedTable({ name: tableName });

      setColumns((asset as BdaDataset).datasetFileType!.columns);
      setSelectedPath((asset as BdaDataset).datasetFileType!.tableName + "/");
    }
  }, [mode, asset, initFrameworks]);

  useEffect(() => {
    initAsset();
    initDatasources();
  }, [initAsset, initDatasources]);

  useEffect(() => {
    if (editingAsset?.id && datasources && selectedDatasource) {
      const selectedDatasource = document.querySelector(".list-wrapper div.datasource-list-item.selected") as HTMLButtonElement;
      if (selectedDatasource) datasourceContainer.current!.scrollTop = selectedDatasource!.offsetTop - datasourceContainer.current!.offsetTop;
    }
  }, [editingAsset, datasources, selectedDatasource]);

  useEffect(() => {
    if (editingAsset?.id && selectedPath && directoryTree) {
      const selectedBranch = document.querySelector(".tree-container button.accordion-toggle.selected") as HTMLButtonElement;
      if (selectedBranch) treeContainer.current!.scrollTop = selectedBranch!.offsetTop - (treeContainer.current?.offsetTop || 0);
    }
  }, [editingAsset, selectedPath, directoryTree]);

  const getTables = useCallback(() => {
    const connectionDetails = getConnectionDetails(selectedDatasource!);
    const body = createQueryDetails(connectionDetails);
    const isMessageBroker = selectedDatasource!.type === 'messageBroker';

    const promise = isMessageBroker
      ? VizClient.getTopics(body, selectedDatasource!.driverType)
      : VizClient.getTables(body, selectedDatasource!.driverType);

    promise.then(
      ({ data: res }) => {
        if (!res) {
          showToast('tables-error', `Error while retrieving ${isMessageBroker ? 'topics' : 'tables'}`, `${isMessageBroker ? 'Topics' : 'Tables'} Error"`, 'danger');
          setTables([]);
          setDatasetSettingsError(true);
          return;
        }

        if (isMessageBroker) {
          const tables = res.topics?.map((t: string) => ({ name: t })) || [];
          setTables(tables);
        }
        else setTables(res.tables || []);
      },
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('tables-error', message, `${isMessageBroker ? 'Topics' : 'Tables'} Error ${status ? `(${status})` : ''}`, 'danger');
        setDatasetSettingsError(true);
      },
    )
  }, [selectedDatasource, showToast]);

  const getDirectoryTree = useCallback((path?: string, jsonPath?: string) => {
    const connectionDetails = getConnectionDetails(selectedDatasource!);
    connectionDetails.path = path || connectionDetails.prefixPath || "";
    if (connectionDetails.prefixPath) delete connectionDetails.prefixPath;
    const body = createQueryDetails(connectionDetails);

    VizClient.browseDirectories(body, selectedDatasource!.driverType)
      .then(
        ({ data: res }) => {
          if (!res) {
            showToast('directories-error', "Error while retrieving directories", "Browsing Error", 'danger');
            setDirectoryTree([]);
            return;
          }
          res.objects.forEach((obj: any) => {
            obj.path = connectionDetails.path + obj.name;
            obj.selected = false;
          });
          if (path) {
            setDirectoryTree(tree => {
              const newTree = cloneDeep(tree!);
              set(newTree, jsonPath!, res.objects);
              return newTree;
            });
          }
          else setDirectoryTree(res.objects);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('directories-error', message, `Browsing Error ${status ? `(${status})` : ''}`, 'danger');
          setDatasetSettingsError(true);
        }
      )
    // eslint-disable-next-line
  }, [selectedDatasource]);

  useEffect(() => {
    if (percent === 100) {
      setDirectoryTree(undefined);
      getDirectoryTree();
    }
  }, [percent, getDirectoryTree]);

  useEffect(() => {
    setDirectoryTree(undefined);
    if (selectedDatasource) {
      if (selectedDatasource.type === "objectStorage") getDirectoryTree();
      else getTables();
    }
  }, [selectedDatasource, getDirectoryTree, getTables]);

  const getDatasetColumns = useCallback(() => {
    const connectionDetails = getConnectionDetails(selectedDatasource!, selectedTable.name, (editingAsset as BdaDataset)?.datasetFileType);
    if (selectedTable.prefixPath !== undefined) connectionDetails.prefixPath = selectedTable.prefixPath + "/";
    if (connectionDetails.table.endsWith("/")) connectionDetails.table = connectionDetails.table.slice(0, -1);

    const body = createQueryDetails(connectionDetails);

    VizClient.getColumns(body, selectedDatasource!.driverType)
      .then(
        ({ data: res }) => {
          if (!res) {
            showToast('columns-error', "Error while retrieving columns", "Columns Error", 'danger');
            setColumns([]);
            setOriginalColumns([]);
            return;
          }

          setColumns(res ? res.columns : [])
          setOriginalColumns(res ? res.columns : []);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('columns-error', message, `Columns Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [selectedDatasource, selectedTable, showToast, editingAsset]);

  useEffect(() => {
    if (selectedTable && selectedDatasource?.type !== 'messageBroker' && mode === 'dataset') getDatasetColumns();
  }, [selectedTable, mode, selectedDatasource, getDatasetColumns]);

  useEffect(() => {
    if (mode === 'model') {
      Models.getModelsFormats()
        .then(
          ({ data }) => setFormatOptions(data.map((i) => { return { label: i, value: i } })),
          (err) => {
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            setFormatOptions([]);
            showToast('formats-error', message, `Formats Error ${status ? `(${status})` : ''}`, 'danger');
          }
        )
    }
  }, [mode, showToast]);

  const handleFileUpload = useCallback(() => {
    showLoader();
    const formData = new FormData();
    // TODO backend should generate uploadId?
    const uploadId = v4().substring(16);
    formData.append('upload_id', uploadId);
    files.forEach(file => {
      formData.append('files', file, file.name);
    });

    const jsonQueryDetails = JSON.stringify({
      queryDetails: [
        {
          host: selectedDatasource?.host,
          accessKey: selectedDatasource?.accessKey,
          secretKey: selectedDatasource?.secretKey,
          bucket: selectedDatasource?.bucket,
          port: selectedDatasource?.port,
          secure: selectedDatasource?.secure,
          prefixPath: selectedPath || selectedDatasource?.prefixPath || "",
        }
      ],
    })
    formData.append('json_query_details', jsonQueryDetails);
    VizClient.uploadFile(formData, selectedDatasource!.driverType)
      .then(
        ({ data }) => {
          hideLoader();
          setProgressLoading(true);
          // TODO listen to job notifications from EventBus
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('file-upload-error', message, `Model ${editingAsset?.id ? 'Update' : 'Creation'} Error ${status ? `(${status})` : ''}`, 'danger');
          setProgressLoading(true);
        }
      );
  }, [selectedDatasource, files, hideLoader, editingAsset, showLoader, showToast, selectedPath]);

  useEffect(() => {
    if (progressLoading) {
      const intervalId = setInterval(() => {
        setPercent((prev) => {
          if ((prev || 0) >= 100) {
            clearInterval(intervalId);
            return prev;
          }
          return (prev || 0) + 1;
        });
      }, 100);
    }
  }, [progressLoading]);

  const submitDataset = useCallback(() => {
    if (!selectedDatasource || (type === "table" && !selectedTable)) return;

    const body = {
      id: editingAsset?.id,
      name: (editingAsset as BdaDataset)?.name,
      description: (editingAsset as BdaDataset)?.description,
      datasetFileType: {
        type,
        columns,
        tableName: selectedDatasource?.type === "objectStorage" ? (editingAsset as BdaDataset)?.path : selectedTable.name,
        path: type === "image" ? (editingAsset as BdaDataset)?.path : null,
      },
      datasource: selectedDatasource,
      accessLevel: (editingAsset as BdaDataset)?.accessLevel,
      tags: (editingAsset as BdaDataset)?.tags,
    };
    if (body.datasetFileType.tableName?.endsWith("/")) body.datasetFileType.tableName = body.datasetFileType.tableName.slice(0, -1);

    showLoader();

    const promise = editingAsset!.id ? Datasets.updateDataset(body as BdaDataset) : Datasets.createDataset(body as BdaDataset);

    promise.then(
      ({ data: newDataset }) => {
        hideLoader();
        showToast('create-dataset-success', `Dataset ${editingAsset?.id ? 'edited' : 'registered'} successfully.`, 'Dataset Success', 'success');
        navigate(`/datasets/${newDataset.id}`);
      },
      () => {
        hideLoader();
        showToast('create-dataset-error', `Could not ${editingAsset?.id ? 'edit' : 'register'} dataset.`, 'Dataset Error', 'danger');
      },
    );
  }, [editingAsset, columns, hideLoader, navigate, selectedDatasource, selectedTable, showLoader, showToast, type]);

  const submitModel = useCallback(() => {
    if (!selectDatasource || !selectedPath) return;

    showLoader();

    const body: BdaModelDTO = {
      ...editingAsset as BdaModelDTO,
      location: selectedPath!,
      datasource: selectedDatasource!,
      framework: frameworks!.find(f => f.id === selectedFramework),
    };

    const promise = editingAsset!.id ? Models.updateModel(body) : Models.createModel(body);

    promise.then(
      () => {
        hideLoader();
        showToast('create-model-success', `Model ${editingAsset?.id ? 'updated' : 'created'} successfully`, `Model ${editingAsset?.id ? 'Updated' : 'Created'}`, 'success');
        navigate("/models");
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('file-upload-error', message, `Model ${editingAsset?.id ? 'Update' : 'Creation'} Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }, [editingAsset, hideLoader, navigate, showToast, showLoader, selectedDatasource, selectedPath, selectedFramework, frameworks]);

  const handleSubmit = useCallback((evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.preventDefault();
    evt.stopPropagation();
    setValidated(true);

    if (form.checkValidity() === false) return;

    if (mode === 'model') submitModel();
    else submitDataset();
  }, [mode, submitDataset, submitModel]);

  const handleFieldChange = (field: keyof BdaModel | keyof BdaDataset) => (evt: any) => {
    const value = field === "tags" || field === "accessLevel" || field === "format" ? evt : evt.target.value;
    setEditingAsset({
      ...editingAsset!,
      [field]: value,
    });
  }

  const selectDatasource = (datasource: Datasource) => () => {
    setDatasetSettingsError(false);
    setTables(undefined);
    setSelectedTable(null);
    setSelectedPath(undefined);
    setSelectedDatasource(datasource);
  }

  const selectTable = (tableName: string) => {
    setEditing(undefined);
    setColumns(undefined);
    const table = tables!.find(table => table.name === tableName);
    setSelectedTable(table);
  }

  const handleFrameworkChange = useCallback((evt: number) => setSelectedFramework(evt), []);
  const handleSearchChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => setSearchText(evt.target.value), []);
  const handleTypeChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => setType(evt.target.value), []);

  const handleBranchShow = useCallback((path: string, jsonPath: string, browse: boolean) => {
    if (browse) getDirectoryTree(path, jsonPath);
  }, [getDirectoryTree]);

  const handleBranchSelect = useCallback((path: string, _jsonPath: string, isTable?: boolean) => {
    setSelectedPath(path);
    setEditingAsset(ds => ({ ...ds, path }));

    if (isTable) {
      setColumns(undefined);
      const name = path.split("/").at(-2);
      const prefixPath = path.split("/").slice(0, -2).join("/");
      setSelectedTable({ name, prefixPath });
    }
  }, []);

  const openUploadModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleColumnTypeChange = (value: string) => {
    setEditingColumn({
      ...editingColumn,
      type: value,
    });
  }

  const revertEdit = (index: number) => () => {
    const newColumns = cloneDeep(columns!);
    newColumns[index].edited = false;
    newColumns[index] = originalColumns![index];
    setColumns(newColumns);
  }

  const editColumn = (index: number) => () => {
    setEditing(index);
    setEditingColumn(cloneDeep(columns![index]));
  }

  const cancelEdit = () => {
    setEditing(undefined);
    setEditingColumn(null);
  }

  const confirmEdit = () => {
    const newColumns = cloneDeep(columns!);
    newColumns[editing!] = editingColumn;
    if (editingColumn.type !== originalColumns![editing!].type) newColumns[editing!].edited = true;
    else newColumns[editing!].edited = false;

    setColumns(newColumns);
    cancelEdit();
  }

  const onCancel = () => navigate(`/${mode}s`);

  const handleModalClose = () => {
    setShowModal(false);
    if (fileUploaded) {
      setPercent(undefined);
      setFiles([]);
      setProgressLoading(false);
    }
  }

  const progressSuccessFormat = useCallback(() => percent + '%', [percent]);

  const handleFilesChange = useCallback((files: File[]) => {
    setFiles(files);
  }, []);

  const renderConnectionSettings = () => {
    return (
      <Row>
        <Col lg={3} className="border-end">
          <Link to="/datasources" target="_blank" rel="noopener noreferrer">
            <h6>
              <span className="me-2">Data source</span>
              <FontAwesomeIcon icon={solid('external-link')} />
            </h6>
          </Link>

          <div className="pb-2 border-bottom">
            <AppSearch value={searchText} onChange={handleSearchChange} />
          </div>

          <div className="list-wrapper border-bottom" ref={datasourceContainer}>
            {datasources
              ? datasources
                .filter(datasource => datasource.name.toLowerCase().includes(searchText.toLowerCase()))
                .map(datasource => (
                  <DatasourceListItem key={datasource.id} datasource={datasource} onClick={selectDatasource(datasource)} selected={selectedDatasource?.id === datasource.id} />
                ))
              : (
                <div className="d-flex py-5 justify-content-center">
                  <AppLoader size={100} />
                </div>
              )
            }
          </div>
        </Col>
        <Col>{renderSettingsContent()}</Col>
      </Row>
    )
  }

  const renderSettingsContent = () => {
    if (!selectedDatasource) {
      return (
        <div className="rounded border bg-light p-5 d-flex flex-column align-items-center justify-content-center text-muted">
          <FontAwesomeIcon icon={solid('faucet-drip')} size="2x" />
          <h5 className="mb-0">Choose data source to see details here.</h5>
        </div>
      )
    }

    if (datasetSettingsError) return (
      <Alert
        showIcon
        type="error"
        message="An error occured with the selected data source. Try again later"
      />
    );

    return (
      <>
        {(selectedDatasource?.type !== 'messageBroker' && mode === 'dataset') && (
          <Form.Group className="d-flex justify-content-between align-items-center mb-3">
            <Form.Label>{modeLabel} Type</Form.Label>

            <div>
              <Form.Check type="radio" id={`${mode}-type-table`} label="Table" name={`${mode}-type`} checked={type === "table"} value="table" onChange={handleTypeChange} inline />
              <Form.Check type="radio" id={`${mode}-type-image`} label="Other" name={`${mode}-type`} checked={type === "image"} value="image" onChange={handleTypeChange} inline />
            </div>
          </Form.Group>
        )}

        {(selectedDatasource.type === "objectStorage") && renderDirectoryTree()}

        {selectedDatasource.type !== "objectStorage" && (
          <>
            <div>
              <Form.Label>{selectedDatasource?.type === 'messageBroker' ? 'Topic' : 'Table'}</Form.Label>
              <Select
                showSearch
                allowClear
                loading={!tables}
                className="w-100"
                placeholder={`Choose ${selectedDatasource?.type === 'messageBroker' ? 'Topic' : 'Table'}`}
                status={(validated && !selectedTable) ? 'error' : undefined}
                optionFilterProp="label"
                onChange={selectTable}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={tables?.map(table => ({
                  label: table.name,
                  value: table.name,
                }))}
                value={selectedTable?.name}
              />
            </div>
          </>
        )}
      </>
    )
  }

  const renderDirectoryTree = useCallback(() => {
    return (
      <>
        <Form.Group controlId={`${mode}-path`} className="mb-2 align-items-center" as={Row}>
          <Col md={3}>
            <Form.Label>Selected Directory Path</Form.Label>
          </Col>
          <Col>
            <div className="d-flex">
              <Col className="pe-2">
                <Form.Control disabled size="sm" placeholder="Select directory from tree below" value={(editingAsset as BdaDataset)!.path || ''} />
              </Col>
              <Button variant="light" className="border px-3" onClick={openUploadModal} size="sm">
                <FontAwesomeIcon icon={solid('upload')} />
                <span className="ms-2">Upload Files</span>
              </Button>
            </div>
          </Col>
        </Form.Group>

        <div className="tree-container border" ref={treeContainer}>
          <Tree elements={directoryTree} onBranchShow={handleBranchShow} onSelect={handleBranchSelect} selectedPath={selectedPath} />
        </div>
      </>
    )
  }, [directoryTree, selectedPath, handleBranchShow, handleBranchSelect, editingAsset, openUploadModal, mode]);

  const renderTableSchema = () => {
    if (!columns) return (
      <div className="p-5 text-center">
        <AppLoader size={300} />
      </div>
    );

    return (
      <div className="mt-3">
        <h6 className="mb-2">Table Schema</h6>
        <div className="list-wrapper">
          <Table className="table-schema" bordered>
            <thead className="bg-light">
              <tr>
                <th>Name</th>
                <th className="text-center" style={{ width: "40%" }}>Type</th>
                <th style={{ width: "100px" }}></th>
              </tr>
            </thead>
            <tbody>
              {columns.map((col, index) => (
                <tr key={col.name} className={classNames({ edit: editing === index, edited: col.edited })}>
                  <td>{col.name}</td>
                  <td className="text-center">
                    {editing !== index && <small className="text-muted text-uppercase">{col.type}</small>}
                    {editing === index && (
                      <Select
                        placeholder="Select column type"
                        className="w-100"
                        onChange={handleColumnTypeChange}
                        options={columnTypes}
                        value={editingColumn.type}
                      />
                    )}
                  </td>
                  <td className="text-center">
                    {editing !== index && (
                      <>
                        {col.edited && (
                          <IconButton type="button" className="p-1" onClick={revertEdit(index)} tooltip="Revert">
                            <FontAwesomeIcon icon={solid('undo')} />
                          </IconButton>
                        )}
                        <IconButton type="button" className="p-1" onClick={editColumn(index)} tooltip="Edit">
                          <FontAwesomeIcon icon={solid('edit')} />
                        </IconButton>
                      </>
                    )}
                    {editing === index && (
                      <>
                        <IconButton type="button" className="text-success p-1 me-2" onClick={confirmEdit} tooltip="Confirm">
                          <FontAwesomeIcon icon={solid('check')} />
                        </IconButton>
                        <IconButton type="button" className="text-danger p-1" onClick={cancelEdit} tooltip="Cancel">
                          <FontAwesomeIcon icon={solid('times')} />
                        </IconButton>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    )
  }

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      <Card.Body>
        <Row className="mb-3">
          <Col>
            <Form.Group controlId="asset-name" className="mb-2">
              <Form.Label>Name</Form.Label>
              <Form.Control size="sm" placeholder={`${modeLabel} Name *`} value={editingAsset!.name || ''} onChange={handleFieldChange('name')} required />
              <Form.Control.Feedback type="invalid">{modeLabel} name is required.</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="asset-tags">
              <Form.Label>Tags</Form.Label>
              <Select mode="tags" className="w-100" placeholder={`${modeLabel} Tags`} value={editingAsset!.tags} onChange={handleFieldChange('tags')} />
            </Form.Group>

            {mode === 'model' && (
              <Form.Group controlId="asset-framework" className="mt-2">
                <Form.Label>Framework</Form.Label>
                <Select
                  className="w-100"
                  placeholder="Select Access Level"
                  onChange={handleFrameworkChange}
                  value={selectedFramework}
                  status={(validated && !selectedFramework) ? 'error' : undefined}
                  loading={!frameworks}
                >
                  {frameworksOptions!.map(option => (
                    <Select.Option key={option.value} value={option.value} label={option.label}>
                      <div className="d-flex align-items-center">
                        <div className="framework-image me-2" style={{ backgroundImage: `url(${option.image})` }}></div>
                        <h6 className="mb-0">{option.label}</h6>
                        <span className="text-muted ms-3">{option.trailing}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
                {validated && !selectedFramework && <Form.Control.Feedback className="d-block" type="invalid">Framework is required.</Form.Control.Feedback>}
              </Form.Group>
            )}
          </Col>

          <Col>
            <Form.Group controlId="asset-description">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" size="sm" rows={4} placeholder={`${modeLabel} description`} value={editingAsset!.description || ''} onChange={handleFieldChange('description')} />
            </Form.Group>
            {mode === 'model' && (
              <Form.Group controlId="asset-format" className="mt-2">
                <Form.Label>Format</Form.Label>
                <Select
                  className="w-100"
                  placeholder="Select Format"
                  onChange={handleFieldChange("format")}
                  options={formatOptions}
                  status={(validated && !(editingAsset as BdaModel)?.format) ? 'error' : undefined}
                />
                {validated && !(editingAsset as BdaModel)?.format && <Form.Control.Feedback className="d-block" type="invalid">Model format is required.</Form.Control.Feedback>}
              </Form.Group>
            )}
          </Col>
        </Row>

        {renderConnectionSettings()}

        {(selectedTable && selectedDatasource?.type !== 'messageBroker' && mode === 'dataset') && renderTableSchema()}

        {validated && (!selectedDatasource || (mode === "dataset" && type === "table" && !selectedTable) || ((mode === "model" || type === "other") && !selectedPath)) && (
          <Form.Control.Feedback type="invalid" className="d-block">You need to select a data source and a table or path to register a {mode}.</Form.Control.Feedback>
        )}
      </Card.Body>

      <div className="d-flex border-top p-2 px-3 justify-content-between">
        <Button type="button" size="sm" variant="light" className="border" onClick={onCancel}>
          <FontAwesomeIcon icon={solid('chevron-left')} />
          <span className="ms-2">Cancel</span>
        </Button>
        <Button type="submit" size="sm" variant="success">
          <FontAwesomeIcon icon={solid('save')} />
          <span className="ms-2">Save</span>
        </Button>
      </div>

      <Modal
        open={showModal}
        onClose={handleModalClose}
        onCancel={handleModalClose}
        width={600}
        title="Upload files"
        footer={
          <div className="d-flex justify-content-between">
            <Button onClick={handleModalClose} size="sm" variant="light" className="border">
              Cancel
            </Button>
            {percent === undefined && (
              <Button disabled={files.length === 0} onClick={handleFileUpload} size="sm" variant="success">
                <FontAwesomeIcon icon={solid('save')} />
                <span className="ms-2">Upload files</span>
              </Button>
            )}
          </div>
        }
      >
        {percent === undefined
          ? (
            <Form.Group controlId="file" className="mb-3">
              <DragAndDropInput onChange={handleFilesChange} />
            </Form.Group>
          )
          : (
            <div className="my-4">
              <Progress status={fileUploaded ? "success" : "normal"} format={progressSuccessFormat} percent={percent} />
              {fileUploaded && (
                <>
                  <Alert
                    className="mt-3"
                    type="success"
                    message={
                      <div className="success-message">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#52c41a" className="w-3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                        <h6 className="text-center mt-2" style={{ color: "#52c41a" }}>File{files.length > 0 && 's'} successfully uploaded!</h6>
                      </div>
                    }
                  />
                </>
              )}
            </div>
          )}
      </Modal>
    </Form>
  )
}

type Props = {
  datasource: Datasource;
  selected?: boolean;
  onClick: React.MouseEventHandler<HTMLDivElement>;
}

export const DatasourceListItem = memo(({ datasource, selected, onClick }: Props) => {
  return (
    <Card className={classNames("datasource-list-item", { selected })} onClick={onClick}>
      <Row className="gx-0 align-items-center mb-2">
        <Col className="overflow-hidden pe-2">
          <h6 className="mb-0 text-truncate">{datasource.name}</h6>
        </Col>

        {selected && (
          <Col xs={1}>
            <FontAwesomeIcon icon={solid('check')} size="lg" className="text-success" />
          </Col>
        )}
      </Row>

      <div className="d-flex justify-content-between align-items-center">
        <UserItem user={datasource.owner!} organization={datasource.organization!} className="sm" />
        <Badge bg="primary" className="w-auto">{datasource.accessLevel}</Badge>
      </div>
    </Card>
  )
});